import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DialogueBase from '../../../components/dialogues/DialogueBase';
import Button from '../../../components/Button';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { TextInput } from './Inputs';
import FormControl from '@material-ui/core/FormControl';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '50rem',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  control: {
    width: '25rem',
  },
  buttons: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    padding: '2rem 0 1rem 0',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'center',
      width: '100%',
    },
  },
  button: {
    marginRight: '1rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '1rem',
    },
  },
  icon: {
    fontSize: '5rem',
    marginRight: '1.5rem',
    marginBottom: '1rem',
  },
}));

const RenameFileDialogue = ({open, onClose, data, renameFile}) => {
  const classes = useStyles();
  const [done, setDone] = useState(false);
  const [error, setError] = useState();
  const [busy, setBusy] = useState(false);
  const fileNameParts = data.name.match(/(.*)\.(.*)/);
  const [fileName, setFileName] = useState(fileNameParts[1]);
  const extension = fileNameParts[2];

  const validateFileName = () => {
    if (fileName === '') {
      setError('File name cannot be empty.');
      return false;
    }
    if (!fileName.match(/^[a-zA-Z0-9\-_ ]+$/)) {
      setError('File name can only contain letters, numbers, spaces and \'-\', and \'_\'.');
      return false;
    }
    return true;
  };

  const onConfirm = async () => {
    setBusy(true);
    const valid = validateFileName();
    if (valid) {
      const { status_code } = await renameFile(data.uuid, `${fileName}.${extension}`);
      if (status_code === 200) {
        setDone(true);
      } else {
        setError('There was a problem renaming this file. Please try again or contact us if the problem persists.');
      }
    }
    setBusy(false);
  };

  useEffect(() => {
    setDone(false);
  }, [open]); // eslint-disable-line

  return (
    <DialogueBase open={open} onClose={onClose} title="Rename file">
      <div className={classes.root}>
        {done ? (
          <Fragment>
            <CheckCircleOutlineIcon color="secondary" className={classes.icon} />
            <Typography variant="h3">The file has been renamed.</Typography>
          </Fragment>
        ) : (
          <FormControl className={classes.control}>
            <Typography variant="h3" gutterBottom>Please enter a new name for this file:</Typography>
            {error !== undefined ? (
              <Typography variant="body2" color="error" gutterBottom>
                {error}
              </Typography>
            ) : null}
            <TextInput
              id="file-name-input"
              autoComplete="filename"
              value={fileName}
              variant="outlined"
              placeholder="New file name"
              error={error !== undefined}
              onChange={e=>setFileName(e.target.value)}
            />
          </FormControl>
        )}
        <div className={classes.buttons}>
          {done ? (
            <Button
              variant="contained"
              label="Back to page"
              cssStyle="large"
              pointer="right"
              onClick={onClose}
            />
          ) : (
            <Fragment>
              <Button
                variant="outlined"
                label="Cancel"
                cssStyle="large"
                pointer="none"
                onClick={onClose}
                className={classes.button}
              />
              <Button
                variant="contained"
                label="Confirm"
                cssStyle="large"
                pointer="none"
                busy={busy}
                onClick={onConfirm}
                className={classes.button}
              />
            </Fragment>
          )}
        </div>
      </div>
    </DialogueBase>
  );
};

RenameFileDialogue.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  renameFile: PropTypes.func.isRequired,
};

export default RenameFileDialogue;
